/**
 * The Quality Evaluation screen (Intelligence): per-contractor dossier tabs over the
 * contractor read model, ADR-025 decision 2's declared formulas.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * PERCENTAGES ONLY. The ADR-014 band instrument is a future station's work and no band,
 * rating, grade or score is computed, named or implied anywhere in this file. The one
 * framing the UI direction legitimises ("level of commitment") labels the response
 * timeliness metric, which the model already computed; the screen renders that number
 * and never invents another.
 *
 * A contractor principal sees exactly the rows the server answered with - its own - so
 * the tabs are one for a contractor and many for the operator side, from the same
 * render path, with no role branch.
 */

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { PortalAccountsBlock } from "../nav/PortalAccounts";
import { HistoryButton } from "../components/HistoryDrawer";
import { listContractorDossiers, type ContractorDossier } from "../api/portfolio";
import { listFindings, type FindingSummary } from "../api/findings";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { AgingHistogram, type AgingBucket } from "../charts/AgingHistogram";
import { DivergingBars, type DivergingRow } from "../charts/DivergingBars";
import { MetricRadar, type MetricSpoke } from "../charts/MetricRadar";
import { ResponseFunnel, type FunnelStage } from "../charts/ResponseFunnel";
import { EmptyState, LoadingLine, Section, Breadcrumbs } from "./parts";
import { useQuery } from "./useQuery";

/** The fixed aging buckets the model publishes, in age order (the contract, not a guess). */
const AGING_LABELS = ["0-30", "31-60", "61-90", "90+"] as const;

function agingBuckets(aging: Readonly<Record<string, number>>): AgingBucket[] {
  return AGING_LABELS.map((label) => ({ label, count: aging[label] ?? 0 }));
}

/** The finding states that count as answered, verified and closed in the funnel. */
const ANSWERED_STATES = new Set([
  "RESPONSE_PROPOSED",
  "AGREED",
  "IMPLEMENTED",
  "PUNCH_COMPLETED",
  "CLOSED",
]);
const VERIFIED_STATES = new Set(["PUNCH_COMPLETED", "CLOSED"]);
const CLOSED_STATES = new Set(["CLOSED"]);

/**
 * The response loop's funnel stages for one contractor (Wave C item 6),
 * computed client-side from the findings register's rows: the dossier read
 * model publishes no per-stage series, so this is the honest computation from
 * what the screen fetches (NOTES.md records the gap and the state mapping).
 */
function funnelStages(findings: readonly FindingSummary[]): FunnelStage[] {
  const counts = {
    raised: findings.length,
    answered: 0,
    verified: 0,
    closed: 0,
  };
  for (const finding of findings) {
    if (ANSWERED_STATES.has(finding.findingStatus)) {
      counts.answered += 1;
    }
    if (VERIFIED_STATES.has(finding.findingStatus)) {
      counts.verified += 1;
    }
    if (CLOSED_STATES.has(finding.findingStatus)) {
      counts.closed += 1;
    }
  }
  return [
    { name: strings.evaluations.funnelStageRaised, value: counts.raised },
    { name: strings.evaluations.funnelStageAnswered, value: counts.answered },
    { name: strings.evaluations.funnelStageVerified, value: counts.verified },
    { name: strings.evaluations.funnelStageClosed, value: counts.closed },
  ];
}

/** The diverging row (Wave C item 2): the dossier's own class distribution. */
function divergingRow(dossier: ContractorDossier): DivergingRow {
  return {
    label: dossier.partyName,
    positive: dossier.findingsByClass.POSITIVE_OBSERVATION ?? 0,
    negative: dossier.findingsByClass.NEGATIVE_OBSERVATION ?? 0,
  };
}

/** The radar's five spokes (Wave C item 7): the dossier facts, as published. */
function radarSpokes(dossier: ContractorDossier): MetricSpoke[] {
  return [
    { label: strings.evaluations.inspections, value: dossier.inspectionsConducted },
    {
      label: strings.evaluations.passRate,
      value: dossier.firstTimePassRate ?? 0,
    },
    {
      label: strings.dashboard.columnTimeliness,
      value: dossier.responseTimeliness.rate ?? 0,
    },
    {
      label: strings.evaluations.meanOpen,
      value: dossier.meanDaysOpenClosed ?? 0,
    },
    { label: strings.evaluations.openActions, value: dossier.overdueActions },
  ];
}

function Dossier({
  dossier,
  findings,
}: {
  dossier: ContractorDossier;
  findings: readonly FindingSummary[];
}): React.JSX.Element {
  const navigate = useNavigate();
  const classes = Object.entries(dossier.findingsByClass).filter(([, count]) => count > 0);
  const ownFindings = findings.filter((one) => one.contractorPartyId === dossier.partyId);
  return (
    <div data-testid="dossier-detail">
      <div className="q-actions-row">
        <HistoryButton
          objectType="PARTY"
          objectId={dossier.partyId}
          recordLabel={dossier.partyName}
        />
      </div>
      <div className="q-ui mb-(--q-s1)">
        <PortalAccountsBlock partyId={dossier.partyId} partyName={dossier.partyName} />
      </div>
      <Section heading={strings.evaluations.commitmentHeading}>
        {dossier.responseTimeliness.rate === null ? (
          <p className="q-thread__body">{strings.evaluations.commitmentAbsent}</p>
        ) : (
          <p className="q-thread__body" data-testid="commitment-rate">
            {strings.evaluations.commitmentBody(dossier.responseTimeliness.rate)}
          </p>
        )}
      </Section>

      <Section heading={strings.dashboard.contractorStatsHeading}>
        <dl className="q-facts" data-testid="dossier-facts">
          <dt>{strings.evaluations.inspections}</dt>
          <dd>{String(dossier.inspectionsConducted)}</dd>
          <dt>{strings.evaluations.passRate}</dt>
          <dd data-testid="pass-rate">
            {dossier.firstTimePassRate === null
              ? ""
              : strings.dashboard.percent(dossier.firstTimePassRate)}
          </dd>
          <dt>{strings.evaluations.meanOpen}</dt>
          <dd>
            {dossier.meanDaysOpenClosed === null
              ? ""
              : strings.dashboard.days(dossier.meanDaysOpenClosed)}
          </dd>
          <dt>{strings.evaluations.openActions}</dt>
          <dd data-testid="overdue-actions">{String(dossier.overdueActions)}</dd>
        </dl>
      </Section>

      <Section heading={strings.evaluations.findingsByClassHeading}>
        {classes.length === 0 ? (
          <EmptyState title={strings.charts.emptyTitle} body={strings.charts.emptyBody} />
        ) : (
          <dl className="q-facts" data-testid="findings-by-class">
            {classes.map(([token, count]) => (
              <FragmentPerClass key={token} token={token} count={count} />
            ))}
          </dl>
        )}
      </Section>

      {/* The diverging class chart (Wave C item 2): the dossier's own rows. */}
      <Section
        heading={strings.evaluations.divergingHeading}
        lede={strings.evaluations.divergingCaption}
      >
        <DivergingBars
          ariaLabel={strings.evaluations.divergingHeading}
          data={[divergingRow(dossier)]}
          categoryAxisLabel={strings.charts.contractorAxis}
          onSelectRow={(side) => {
            // The findings register carries no party filter parameter, so the
            // click-through lands on the class-filtered register (NOTES.md).
            void navigate(
              side === "positive"
                ? "/findings?class=POSITIVE_OBSERVATION"
                : "/findings?class=NEGATIVE_OBSERVATION",
            );
          }}
        />
      </Section>

      {/* The response loop's funnel (Wave C item 6), for this contractor. */}
      <Section heading={strings.evaluations.funnelHeading} lede={strings.evaluations.funnelCaption}>
        <ResponseFunnel
          ariaLabel={strings.evaluations.funnelHeading}
          data={funnelStages(ownFindings)}
          onSelectStage={(_stage, index) => {
            // The click-through contract: each funnel stage lands on the
            // findings register filtered to the states that count there.
            const stageStates = [
              [],
              ["RESPONSE_PROPOSED", "AGREED", "IMPLEMENTED", "PUNCH_COMPLETED", "CLOSED"],
              ["PUNCH_COMPLETED", "CLOSED"],
              ["CLOSED"],
            ][index]?.join(",");
            void navigate(
              stageStates === undefined ? "/findings" : `/findings?state=${stageStates}`,
            );
          }}
        />
      </Section>

      {/* The five dossier metrics' radar (Wave C item 7). */}
      <Section heading={strings.evaluations.radarHeading} lede={strings.evaluations.radarCaption}>
        <MetricRadar ariaLabel={strings.evaluations.radarHeading} data={radarSpokes(dossier)} />
      </Section>

      <Section heading={strings.evaluations.agingHeading}>
        <AgingHistogram
          ariaLabel={strings.evaluations.agingHeading}
          buckets={agingBuckets(dossier.openNcrCarAging)}
        />
      </Section>
    </div>
  );
}

function FragmentPerClass({ token, count }: { token: string; count: number }): React.JSX.Element {
  return (
    <>
      <dt>{termForClass(token)}</dt>
      <dd>{String(count)}</dd>
    </>
  );
}

function termForClass(token: string): string {
  const table: Record<string, string> = { ...strings.terms.findingClasses };
  return table[token] ?? strings.terms.fallback(token);
}

export function EvaluationsPage(): React.JSX.Element {
  const token = useToken();
  const dossiers = useQuery((signal) => listContractorDossiers(token, signal), [token]);
  // The funnel's feed (Wave C item 6): the findings register's rows, sliced per
  // contractor client-side (the dossier read model publishes no stage series).
  const findings = useQuery((signal) => listFindings(token, { limit: 500, signal }), [token]);
  const [selected, setSelected] = useState<string | null>(null);
  // The dossier seam's click-through: ?party= deep-links THIS contractor's evaluation
  // view (from the dashboard dossier rows and charts); the URL, not local state, is
  // the source of truth when it is present, so browser back restores the tab.
  const [parameters, setParameters] = useSearchParams();
  const partyParam = parameters.get("party");

  const rows = dossiers.kind === "ready" ? dossiers.data : [];
  const first = rows.length > 0 ? rows[0] : undefined;
  const activePartyId = partyParam ?? selected ?? first?.partyId ?? null;
  const active = rows.find((one) => one.partyId === activePartyId);

  const select = (partyId: string): void => {
    setSelected(partyId);
    const next = new URLSearchParams(parameters);
    next.set("party", partyId);
    setParameters(next);
  };

  return (
    <>
      {/* The breadcrumb (M5 P6 seat V #7): where am I — the selected contractor's
          dossier is the detail view inside this register. */}
      <Breadcrumbs
        trail={
          active
            ? [{ label: strings.evaluations.heading }, { label: active.partyName }]
            : [{ label: strings.evaluations.heading }]
        }
      />
      <h2 className="q-shell__heading">{strings.evaluations.heading}</h2>
      <p className="q-shell__lede">{strings.evaluations.lede}</p>

      {dossiers.kind === "loading" && <LoadingLine label={strings.evaluations.loading} />}
      {dossiers.kind === "failed" && (
        <div className="q-refusal" role="alert">
          <p className="q-refusal__heading">{strings.evaluations.failed}</p>
        </div>
      )}
      {dossiers.kind === "ready" &&
        (rows.length === 0 ? (
          <EmptyState
            title={strings.evaluations.dossierEmpty}
            body={strings.evaluations.dossierEmpty}
          />
        ) : (
          <>
            <div className="q-row" role="tablist" aria-label={strings.evaluations.heading}>
              {rows.map((dossier) => (
                <button
                  key={dossier.partyId}
                  type="button"
                  role="tab"
                  aria-selected={activePartyId === dossier.partyId}
                  className={
                    "q-button q-button--quiet" +
                    (activePartyId === dossier.partyId ? " q-mini--primary" : "")
                  }
                  data-testid="dossier-tab"
                  onClick={() => {
                    select(dossier.partyId);
                  }}
                >
                  {dossier.partyName}
                </button>
              ))}
            </div>
            {active !== undefined && (
              <Dossier
                dossier={active}
                findings={findings.kind === "ready" ? findings.data.items : []}
              />
            )}
          </>
        ))}
    </>
  );
}
